import {
  emailCompositionDefaults,
  type EmailCompositionValue,
} from '@kula-ai/components';
import {
  toIdString,
  toSelectOptions as toOptions,
  isSameEmailValue,
  type EmailUser,
} from '@/components/shared/email/emailValue';
import type { AutoRejectEmailFormValues } from '../autoRejectEmail.types';

export type AutoRejectFromUser = EmailUser;

/** A rejection template row from `/ats_comm_templates`. */
export interface AutoRejectTemplate {
  id: number;
  name: string;
  subject?: string;
  body?: string;
  from?: string;
  cc?: string[];
  bcc?: string[];
  attachments?: unknown[];
}

/**
 * `useEmailField` used `valueKey: 'id'`, so the form holds user ids for
 * from/cc/bcc and the save payload resolves senders by id. Options keep that
 * contract; kula `Select` keys on strings, hence the stringify.
 */
export const toSelectOptions = (users: AutoRejectFromUser[] = []) =>
  toOptions(users, 'id');

export const toTemplateOptions = (templates: AutoRejectTemplate[] = []) =>
  templates.map((template) => ({
    label: template.name ?? '',
    value: String(template.id),
    data: template,
  }));

/** Seeds the composer from the island's Formik values. */
export const toEmailValue = (
  values: Partial<AutoRejectEmailFormValues> | undefined | null
): EmailCompositionValue => ({
  ...emailCompositionDefaults,
  // On edit-restore `from` holds the sender's *email* while the options are
  // keyed by id, so prefer the id off `fromUserObject`. Without this the From
  // row renders empty and nothing resolves back to a sender.
  from: toIdString(
    (values?.fromUserObject as { id?: number } | null)?.id ?? values?.from
  ),
  // The recipient is the rejected candidate, resolved server-side at send time.
  to: '',
  cc: (values?.cc ?? []).map(toIdString),
  bcc: (values?.bcc ?? []).map(toIdString),
  subject: values?.subject ?? '',
  content: values?.body ?? '',
  template:
    toIdString((values?.selectedTemplate as { id?: number } | null)?.id) ||
    null,
});

/** Maps a composer snapshot back onto the Formik slice the save path reads. */
export const fromEmailValue = (
  value: EmailCompositionValue,
  resolveUser: (_id: string) => AutoRejectFromUser | undefined
): Partial<AutoRejectEmailFormValues> => {
  const sender = resolveUser(value.from);
  const toId = (id: string) => String(resolveUser(id)?.id ?? id);

  return {
    from: sender?.id === undefined ? value.from : String(sender.id),
    // The save payload resolves the sender from `fromUserObject`, so only
    // overwrite it when this snapshot actually names one. Emitting null on an
    // unresolved sender would blank a saved sender on an unrelated body edit.
    ...(sender
      ? { fromUserObject: sender as unknown as Record<string, unknown> }
      : {}),
    cc: (value.cc ?? []).map(toId),
    bcc: (value.bcc ?? []).map(toId),
    subject: value.subject,
    body: value.content,
  };
};

export { isSameEmailValue };

/**
 * The picked template also carries sender, cc/bcc and attachments — kula only
 * applies the body, so the rest is merged here to match what the old
 * `handleTemplateChange` wrote onto the form.
 */
export const templateSideEffects = (
  template: AutoRejectTemplate | null,
  currentFrom: string
): Partial<AutoRejectEmailFormValues> =>
  template
    ? {
        selectedTemplate: template as unknown as Record<string, unknown>,
        from: template.from || currentFrom,
        cc: template.cc ?? [],
        bcc: template.bcc ?? [],
        attachments: template.attachments ?? [],
      }
    : { selectedTemplate: null, subject: '', body: '' };
